import './Card.scss';
import React, { memo, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Link } from 'react-router';
import { apiGet } from '../../js/api';
import Helpers from '../../js/helpers';
import Server from '../../js/config/_server';
import Loader from '../loader/Loader';
import ImageStatic from '../imageStatic/ImageStatic';
import {
    REQUESTS,
    ROUTES,
    RESPONSE,
    DEFAULT_MEDIA_ORDER
} from '../../js/_constants';
import { BRAND_LETTER } from '../../js/config/_global';
import { useTranslation } from 'react-i18next';
import { type DistributedMedium, type Playlist, type Device } from '../../api/types';
import {
    type ElasticSearchResponse,
    type CollectionResponse
} from '../../api/types/response';
import _server from '../../js/config/_server';
import queryString from 'query-string';

type DashboardCardData = {
    type: string;
    title: string;
    query?: string;
    class: string;
    action?: string;
    position?: string;
    search?:
        | {
              page: number;
              type: string | string[];
          }
        | string;
};

type CardProps = {
    data: DashboardCardData;
    playlists: { loading: boolean; items: Playlist[] };
};

const Card = ({ data }: CardProps) => {
    const { t } = useTranslation();

    const [loading, setLoading] = useState<boolean>(true);
    const [amount, setAmount] = useState<string | number | undefined>(
        undefined
    );
    const [isRequesting, setIsRequesting] = useState<boolean>(false);
    const [isMounted, setIsMounted] = useState<boolean>(true);
    const [activeTab, setActiveTab] = useState<number>(0);
    const controller = new AbortController();
    const signal = controller.signal;

    /**
     * we need synchronous flag for cancellable as the state value for isRequesting
     * does not update immediately during the data effect
     */
    const isCancellable = useRef(false);

    useEffect(() => {
        if (data.query) {
            setIsRequesting(true);
            isCancellable.current = true;
            if (!isRequesting) {
                apiGet<ElasticSearchResponse<DistributedMedium>>(
                    `${Server.baseUrl}${Server.services.search}?${data.query}`,
                    { accept: REQUESTS.ACCEPT.JSON_LD, signal }
                )
                    .then(response => {
                        if (isMounted) {
                            setAmount(response[RESPONSE.TOTAL]);
                        }
                    })
                    .catch(error => error)
                    .finally(() => {
                        isCancellable.current = false;

                        setIsRequesting(false);
                        setLoading(false);
                    });
            }
        }
    }, [data]);


    useEffect(() => {
        const lastSearch = localStorage.getItem('last-search');
        let searchParameters= '';
        if(lastSearch?.includes('Tag: ')){
            searchParameters = `tag=${lastSearch.split('Tag: ')[1]}&page=1`;
        } else if(lastSearch?.includes('Title: ')){
            searchParameters = `title=${lastSearch.split('Title: ')[1]}&page=1`;
        } else {
            searchParameters = `query=${lastSearch}&page=1`;
        }
        if (data.title === 'dashboard_results') {
            if (lastSearch) {
                setIsRequesting(true);
                isCancellable.current = true;
                if (!isRequesting) {
                    apiGet<ElasticSearchResponse<DistributedMedium>>(
                        `${Server.baseUrl}${Server.services.search}?${searchParameters}`,
                        { accept: REQUESTS.ACCEPT.JSON_LD, signal }
                    )
                        .then(response => {
                            if (isMounted) {
                                setAmount(response[RESPONSE.TOTAL]);
                            }
                        })
                        .catch(error => error)
                        .finally(() => {
                            isCancellable.current = false;

                            setIsRequesting(false);
                            setLoading(false);
                        });
                }
            } else {
                setLoading(false);
            }
        }
    }, [data]);

    useEffect(() => {
        if (data.title === 'dashboard_playlists') {
            isCancellable.current = true;

            const url =
                _server.baseUrl +
                _server.services.playlists.userPlaylists;

            const params = {
                brand: BRAND_LETTER,
                createdByCurrentUser: false
            } as any;

            apiGet<CollectionResponse<Playlist>>(url, { params })
                .then(playlists => {
                    setAmount(String(playlists['hydra:totalItems']));
                })
                .finally(() => {
                    isCancellable.current = false;

                    setLoading(false);
                });
        }
    }, [data]);

    useEffect(() => {  
      if (data.title === 'dashboard_devices'){         
            isCancellable.current = true;

            const url =
                _server.baseUrl +
                _server.services.devices.userDevices;

            const params = {
                brand: BRAND_LETTER,
            } as any;

            apiGet<CollectionResponse<Device>>(url, { params })
                .then(devices => {
                    setAmount(String(devices['hydra:totalItems']));
                })
                .finally(() => {
                    isCancellable.current = false;

                    setLoading(false);
                });
        }
        if (data.title === 'dashboard_playlists') {
            setActiveTab(1);
        }
    }, [data]);

    useEffect(() => {            
        return () => {
            if (isCancellable.current) {
                controller.abort();
                setIsMounted(false);
            }
        };
    }, []);

    const generateLink = () => {
        const { search, action } = data;
        const lastSearch = localStorage.getItem('last-search');
        let query = '', tag = '', title = ''
        if(lastSearch?.includes('Tag: ')){
            tag = lastSearch.split('Tag: ')[1];
        } else if(lastSearch?.includes('Title: ')){
            title = lastSearch.split('Title: ')[1];
        } else {
            query = lastSearch || '';
        }
        let url;
        if (data.title === 'dashboard_results') {
            url = `${ROUTES.MEDIA_LIBRARY}?${queryString.stringify({
                'order[relevance]': DEFAULT_MEDIA_ORDER,
                page: 1,
                ...(tag && { tag }),
                ...(title && { title }),
                ...(query && { query })
            })}`;
            return url;
        }

        if (data.title === 'dashboard_playlists') {
            url = { pathname: action };
            return url;
        }
        if (data.title === 'dashboard_devices') {
            return action;
        }
        if (typeof search === 'object') {
            url = Helpers.addParamsToUrl(ROUTES.MEDIA_LIBRARY, {
                page: search.page,
                type: search.type
            });
        } else if (action) {
            url = action;
        }

        return url;
    };

    const generateDataTestId = () => {
        if (data.title === 'dashboard_results') {
            return 'dashboard-card-results';
        }
        if (data.title === 'dashboard_playlists') {
            return 'dashboard-card-playlists';
        }
        if (data.title === 'dashboard_devices') {
            return 'dashboard-card-devices';
        }
        if (data.title === 'dashboard_images') {
            return 'dashboard-card-images';
        }
        if (data.title === 'dashboard_videos') {
            return 'dashboard-card-videos';
        }
        return 'card';
    };

    const classObject = {
        'b-card': true,
        'is-loading': loading
    };

    if (data.title === 'dashboard_results' && (amount === 0 || !localStorage.getItem('last-search'))) {
        classObject['un-clickable'] = true;
    }

    if (typeof data.type !== 'undefined') {
        classObject[data.type] = true;
    } else {
        classObject['small'] = true;
    }

    if (typeof data.class !== 'undefined') {
        classObject[data.class] = true;
    }

    const classes = classNames(classObject);

    return (
        <Link className={classes} to={generateLink()} state={{ activeTab: activeTab }} data-testid={generateDataTestId()}>
            {data.position && (
                <ImageStatic
                    imagePath={`./assets/${data.position}`}
                    altText='dashboard card image'
                    classnames='card-image'
                />
            )}

            <div className="title-container">
                {loading ? (
                    <Loader isSmall />
                ) : (
                    <h1 className="items-count" data-testid={generateDataTestId() + '-count'}>{amount ? amount : 0}</h1>
                )}
                <h2 className="card-title">{t(data.title)}</h2>
            </div>
        </Link>
    );
};

export default memo(Card);
